import { DecimalPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CompilingWidgetService } from '../../../services/compiling/widget.service';
import {
  emptyMultiplierAnswer,
  isDefaultMultiplierOption,
  isMultiplierAnswer,
  MultiplierAnswer,
  MultiplierOption,
  MultiplierRadioButtonCustomization,
  multiplierScoredValue,
} from './multiplier-answer';

/** Read-only: the chosen option checked, its value beside it, every multiplier shown. */
@Component({
  selector: 'lib-multiplier-radiobutton-compiled',
  imports: [FormsModule, RadioButtonModule, DecimalPipe],
  template: `
    @let widget = widgetService.widget();
    @let formControl = widgetService.formControl();
    @if (widget && formControl) {
      @let answer = currentAnswer();
      <div class="flex w-full flex-col gap-2">
        <label [for]="groupId" class="whitespace-normal text-justify">
          {{ widget.customization.description }} (max
          {{ widgetService.maxPoints() | number: '1.0-1' }}
          pt)
        </label>
        <div [class]="'p-fluid flex' + topologyStyle">
          @for (
            option of widget.customization.options;
            track option.key;
            let i = $index
          ) {
            <div class="flex flex-row items-center gap-2 whitespace-nowrap">
              @let id = groupId + '_option_' + i;
              <p-radioButton
                [autofocus]="false"
                [inputId]="id"
                [name]="groupId"
                [value]="option.key"
                [ngModel]="answer.option"
                [ngModelOptions]="{ standalone: true }"
                [disabled]="true"
              ></p-radioButton>
              <label [for]="id">
                {{ option.label }}
                @if (!isDefault(option)) {
                  <span class="text-slate-500">
                    (×{{ option.multiplier | number: '1.0-3' }})
                  </span>
                  @if (option.key === answer.option) {
                    @let shown =
                      scoredValue(option, answer, widget.customization.decimal);
                    @if (shown !== null) {
                      <span class="font-semibold">
                        — {{ shown | number: '1.0-3' }}
                      </span>
                    }
                  }
                }
              </label>
            </div>
          }
        </div>
      </div>
    }
  `,
  styleUrls: ['../style.css'],
  providers: [],
})
export class MultiplierRadioButtonCompiledComponent {
  protected widgetService = inject(
    CompilingWidgetService<
      MultiplierRadioButtonCustomization,
      MultiplierAnswer
    >,
  );

  protected topologyStyle = ' flex-row flex-wrap gap-x-5 gap-y-2';
  protected groupId = `multiplierRadioButton_${this.widgetService.uniqueId}`;
  protected readonly isDefault = isDefaultMultiplierOption;

  /** What the score is computed on, not the raw saved value. */
  protected scoredValue(
    option: MultiplierOption,
    answer: MultiplierAnswer,
    decimal: boolean,
  ): number | null {
    return multiplierScoredValue(option, answer.value, decimal);
  }

  protected currentAnswer(): MultiplierAnswer {
    const value = this.widgetService.formControl()?.value;
    return isMultiplierAnswer(value) ? value : emptyMultiplierAnswer();
  }
}
